import { getConfig } from '../config'
import { onCookiesAccepted } from '../cookies-blocker'
import type { CookieCategory } from '../cookies-blocker/types'
import type {
  AnalyticsEventName,
  AnalyticsQueueEntry,
  AnalyticsVendor,
  PickAnalyticsEventAttributes,
} from './types'
import { GoogleTagManager } from './vendors/google-tag-manager/integration'
import { mParticle } from './vendors/mparticle/integration'
import { Perseus } from './vendors/perseus/integration'
import { consoleLog } from '@/tools/console-log'
import { createGlovoError, getErrorMessage, glovoRum } from '@/tools/o11y/client'

const QUEUE_MAX_LENGTH = 1000

export class Analytics {
  constructor(vendors: AnalyticsVendor[]) {
    this.vendors = vendors
  }

  private vendors: AnalyticsVendor[]
  private isInitializing: boolean = false
  private isInitialized: boolean = false
  private eventQueue: AnalyticsQueueEntry[] = []

  private get areAllVendorsInitialized() {
    return this.vendors.every((vendor) => vendor.isInitialized)
  }

  public async init() {
    const { isBrowser } = getConfig()
    if (!isBrowser) {
      return
    }

    if (this.isInitializing || this.isInitialized) {
      return
    }
    this.isInitializing = true

    void this.initVendorsWithCategory('essential')
    void this.initVendorsWithCategory('functional')
    void this.initVendorsWithCategory('marketing')

    this.isInitializing = false
    this.isInitialized = true
  }

  private getVendorsWithCategory = (category: CookieCategory) =>
    this.vendors.filter((vendor) => vendor.consentCategory === category)

  private async initVendorsWithCategory(category: CookieCategory) {
    const categoryVendors = this.getVendorsWithCategory(category)
    await onCookiesAccepted(category)

    for (const vendor of categoryVendors) {
      try {
        await vendor.init()
        consoleLog('Analytics', 'init', { vendor: vendor.name, consent: vendor.consentCategory })
        // eslint-disable-next-line @glovo/custom-rules/always-forward-error
      } catch (error) {
        // Because vendors seem to be blocked/conflict with browser extensions, is common to see
        // errors here that add a lot of noise on our monitoring tools. To avoid that we are going
        // to track these fails as RUM actions (and build monitors on top of that if necessary).
        glovoRum.addAction('analytics_service.vendor_init.error', {
          message: '[Analytics] Failed to initialize vendor',
          vendorInitError: getErrorMessage(error),
          vendorName: vendor.name,
        })
      }
    }

    /*
     * Send the queue events to each one of the vendors of the category that can handle it.
     * NOTE: We are not using  "this.trackEvent" method as it would lead to events being re-added
     * to the queue, due to "this.trackEvent" internal check and logic.
     */
    if (this.eventQueue.length) {
      for (let i = 0; i < this.eventQueue.length; i++) {
        for (const vendor of categoryVendors) {
          const event = this.eventQueue[i]
          if (vendor.canHandle(event.eventName)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            vendor.trackEvent(event.eventName, event.eventData as any)
          }
        }
      }
    }

    /**
     * If the current category contains the last vendors to be initialized, clear the queue
     * as is no longer needed.
     */
    if (this.areAllVendorsInitialized) {
      this.eventQueue = []
    }
  }

  private addToQueue(eventName: AnalyticsEventName, eventData: unknown) {
    this.eventQueue.push({ eventName, eventData })
    /**
     * Because some vendors may never be initialized (in case user never grant permissions),
     * we need to limit the queue size to avoid memory overuse, as the queue would keep growing
     * infinitely.
     */
    if (this.eventQueue.length > QUEUE_MAX_LENGTH) {
      this.eventQueue.shift()
    }
  }

  public trackEvent<EVENT_NAME extends AnalyticsEventName>(
    eventName: EVENT_NAME,
    eventData: PickAnalyticsEventAttributes<EVENT_NAME>
  ) {
    const { isBrowser } = getConfig()
    if (!isBrowser) {
      return
    }

    /**
     * If client is not initialised add event to the queue and stop execution. Events will be sent
     * when the client is initialized.
     */
    if (!this.isInitialized) {
      this.addToQueue(eventName, eventData)
      return
    }

    /**
     * If not all vendors are initialized, add event to the queue so it can be sent later
     * when the pending vendors are initialized (but continue as they will be sent by the
     * already initialized vendors, in case they can handle the event)
     */
    if (!this.areAllVendorsInitialized) {
      this.addToQueue(eventName, eventData)
    }

    for (const vendor of this.vendors) {
      if (!vendor.isInitialized || !vendor.canHandle(eventName)) {
        continue
      }

      const eventAttributes = { eventName, eventVendor: vendor.name, eventDataRaw: eventData }
      try {
        vendor.trackEvent(eventName, eventData)
        glovoRum.addAction('analytics_service.track_event.success', eventAttributes)
        // eslint-disable-next-line @glovo/custom-rules/always-forward-error
      } catch (e) {
        glovoRum.addError(createGlovoError(e), {
          errorType: 'analytics_service.track_event.error',
          ...eventAttributes,
        })
      }
    }
  }
}

export const analytics = new Analytics([new mParticle(), new Perseus(), new GoogleTagManager()])
